import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";

export default function CanvasPropertiesPanel({
  canvas,
  decks = [],
  languages = [],
  onChange,
  onDelete,
  saving = false,
  error = ""
}) {
  const [expandedDecks, setExpandedDecks] = useState({});

  if (!canvas) return null;
  // Filter out deck 0 and optionally hide other users' decks
  let filteredDecks = decks.filter(deck => Number(deck.deck_id) !== 0);
  const { user } = useAuth();
  const currentUserId = user?.user_id || user?.id;
  if (!canvas.show_user_decks) {
    // Show: system decks, user's own decks
    filteredDecks = filteredDecks.filter(deck => deck.is_system_deck || deck.owner_id === currentUserId);
  } else {
    // Show: system decks, user's own decks, AND other users' public decks where owner can_share
    filteredDecks = filteredDecks.filter(deck => 
      deck.is_system_deck || 
      deck.owner_id === currentUserId || 
      (deck.is_public && deck.owner_can_share)
    );
  }
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
      <h3>Story Properties</h3>
      <form style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        <div id="tour-rename-story" style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
          <label style={{ minWidth: 120, marginRight: 16, fontWeight: 'bold', textAlign: 'right' }}>
            Story Name:
          </label>
          <input
            type="text"
            value={canvas.name || ""}
            onChange={e => onChange && onChange({ ...canvas, name: e.target.value })}
            style={{
              flex: 1,
              padding: 8,
              border: "1px solid #ddd",
              borderRadius: 4,
              fontSize: 14
            }}
            disabled={!onChange}
          />
        </div>
        <div id="tour-language-select" style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
          <label style={{ minWidth: 120, marginRight: 16, fontWeight: 'bold', textAlign: 'right' }}>
            Language:
          </label>
          <select
            value={canvas.language || "en"}
            onChange={e => onChange && onChange({ ...canvas, language: e.target.value })}
            style={{
              flex: 1,
              padding: 8,
              border: "1px solid #ddd",
              borderRadius: 4,
              fontSize: 14
            }}
            disabled={!onChange}
          >
            {languages.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label id="tour-show-user-decks" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={canvas.show_user_decks ?? false}
              onChange={e => onChange && onChange({ ...canvas, show_user_decks: e.target.checked })}
              style={{ marginRight: 8 }}
              disabled={!onChange}
            />
            <span style={{ fontWeight: "bold" }}>
              Show other users' decks
            </span>
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label id="tour-shuffle-extension" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={canvas.shuffle_extensions || false}
              onChange={e => onChange && onChange({ ...canvas, shuffle_extensions: e.target.checked })}
              style={{ marginRight: 8 }}
              disabled={!onChange}
            />
            <span style={{ fontWeight: "bold" }}>
              Shuffle Extension Decks into Main deck
            </span>
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label id="tour-allow-duplicate-draws" style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={canvas.allow_duplicate_draws || false}
              onChange={e => onChange && onChange({ ...canvas, allow_duplicate_draws: e.target.checked })}
              style={{ marginRight: 8 }}
              disabled={!onChange}
            />
            <span style={{ fontWeight: "bold" }}>
              Cards can be drawn multiple times
            </span>
          </label>
        </div>
        <div id="tour-custom-background-images" style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
          <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={canvas.showImages || false}
              onChange={e => onChange && onChange({ ...canvas, showImages: e.target.checked })}
              style={{ marginRight: 8 }}
              disabled={!onChange}
            />
            <span style={{ fontWeight: "bold" }}>
              Show Custom Background Images on Cards
            </span>
          </label>
        </div>
        <div id="tour-canvas-bg" style={{ marginBottom: 16, display: 'flex', alignItems: 'center' }}>
          <label style={{ minWidth: 120, marginRight: 16, fontWeight: 'bold', textAlign: 'right' }}>
            Canvas BG:
          </label>
          <select
            value={canvas.background_image || "default"}
            onChange={e => onChange && onChange({ ...canvas, background_image: e.target.value })}
            style={{
              flex: 1,
              padding: 8,
              border: "1px solid #ddd",
              borderRadius: 4,
              fontSize: 14,
              cursor: 'pointer'
            }}
            disabled={!onChange}
          >
            <option value="default">✨ Modern Dark</option>
            <option value="Background DOW.png">🗺️ Build your World Playmat</option>
            <option value="Background LMD.png">🗺️ Weave your Lore Playmat</option>
            <option value="Background TSE.png">🗺️ Tell Your Story Playmat</option>
          </select>
        </div>
        <div id="tour-deck-selector" style={{ marginBottom: 16, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
            Selected Decks:
          </label>
          <div style={{
            border: "1px solid #ddd",
            borderRadius: 4,
            flex: 1,
            overflowY: "auto",
            padding: 8,
            minHeight: 0,
            position: 'relative'
          }}>
            {filteredDecks.length === 0 ? (
              <div style={{ color: "#666", textAlign: "center", padding: 16 }}>
                No decks available.
              </div>
            ) : (
              (() => {
                // Helper to get display name for sorting
                const getDeckName = deck =>
                  deck.translations?.[canvas.language]?.name ||
                  deck.translations?.en?.name ||
                  deck.name ||
                  `Deck ${deck.deck_id}`;
                // Split into main and extension decks
                const mainDecks = filteredDecks.filter(deck => !deck.is_extension);
                const extensionDecks = filteredDecks.filter(deck => deck.is_extension);
                // Sort main decks alphabetically
                const sortedMainDecks = [...mainDecks].sort((a, b) => getDeckName(a).localeCompare(getDeckName(b)));
                // Group extension decks by main_deck_id and sort them
                const extensionsByMain = {};
                for (const ext of extensionDecks) {
                  if (!extensionsByMain[ext.main_deck_id]) extensionsByMain[ext.main_deck_id] = [];
                  extensionsByMain[ext.main_deck_id].push(ext);
                }
                for (const key in extensionsByMain) {
                  extensionsByMain[key].sort((a, b) => getDeckName(a).localeCompare(getDeckName(b)));
                }
                return sortedMainDecks.map(mainDeck => {
                  const hasExtensions = extensionsByMain[mainDeck.deck_id] && extensionsByMain[mainDeck.deck_id].length > 0;
                  const isExpanded = expandedDecks[mainDeck.deck_id];

                  return (
                    <div key={mainDeck.deck_id} style={{ marginBottom: 8 }}>
                      {/* Main deck row */}
                      {(() => {
                        const isSelected = (canvas.selected_decks || []).some(id => Number(id) === mainDeck.deck_id);
                        return (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 0",
                              cursor: onChange ? "pointer" : "default"
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={e => {
                                if (!onChange) return;
                                const currentSelected = canvas.selected_decks || [];
                                let newSelected;
                                if (e.target.checked) {
                                  newSelected = [...currentSelected, mainDeck.deck_id];
                                } else {
                                  newSelected = currentSelected.filter(id => Number(id) !== mainDeck.deck_id);
                                }
                                onChange({ ...canvas, selected_decks: newSelected });
                              }}
                              style={{ marginRight: 12 }}
                              disabled={!onChange}
                            />
                            <div style={{
                              width: 32,
                              height: 32,
                              marginRight: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}>
                              {<img
                                src={mainDeck.deck_symbol_path}
                                alt="Deck Symbol"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                              }
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold" }}>
                                {getDeckName(mainDeck)}
                              </div>
                            </div>
                            {hasExtensions && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedDecks(prev => ({
                                    ...prev,
                                    [mainDeck.deck_id]: !prev[mainDeck.deck_id]
                                  }));
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  fontSize: "16px",
                                  padding: "4px 12px",
                                  color: "#666",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center"
                                }}
                                title={isExpanded ? "Collapse extensions" : "Expand extensions"}
                              >
                                {isExpanded ? "▲" : "▼"}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      {/* Extension decks under this main deck */}
                      {isExpanded && (extensionsByMain[mainDeck.deck_id] || []).map(extDeck => {
                        const isSelected = (canvas.selected_decks || []).some(id => Number(id) === extDeck.deck_id);
                        return (
                          <div
                            key={extDeck.deck_id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px 0 8px 32px",
                              cursor: onChange ? "pointer" : "default"
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={e => {
                                if (!onChange) return;
                                const currentSelected = canvas.selected_decks || [];
                                let newSelected;
                                if (e.target.checked) {
                                  newSelected = [...currentSelected, extDeck.deck_id];
                                } else {
                                  newSelected = currentSelected.filter(id => Number(id) !== extDeck.deck_id);
                                }
                                onChange({ ...canvas, selected_decks: newSelected });
                              }}
                              style={{ marginRight: 12 }}
                              disabled={!onChange}
                            />
                            <div style={{
                              width: 32,
                              height: 32,
                              marginRight: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}>
                              {<img
                                src={extDeck.deck_symbol_path}
                                alt="Deck Symbol"
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                              }
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: "bold" }}>
                                {getDeckName(extDeck)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                });
              })()
            )}
          </div>
        </div>
        {error && (
          <div style={{ color: 'red', marginBottom: 16, fontSize: 14 }}>
            {error}
          </div>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={saving}
            style={{
              width: "100%",
              padding: "10px 0",
              backgroundColor: "#e74c3c",
              color: "white",
              border: "none",
              borderRadius: 6,
              cursor: saving ? "wait" : "pointer",
              fontSize: 16,
              fontWeight: 600,
              opacity: saving ? 0.6 : 1,
              marginTop: 'auto',
              boxShadow: '0 2px 8px #0002',
            }}
          >
            Delete Canvas
          </button>
        )}
      </form>
    </div>
  );
} 